import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import DOMPurify from "dompurify";
import { http } from "../../lib/api";
import PremiumLayout from "./PremiumLayout";

function formatDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });
  } catch { return ""; }
}

export default function PremiumBlogDetail() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [error, setError] = useState(null);
  const [others, setOthers] = useState([]);

  useEffect(() => {
    http.get(`/marketing/blog/slug/${slug}`)
      .then(({ data }) => setPost(data))
      .catch(() => setError("Post not found"));
    http.get("/marketing/blog?limit=6")
      .then(({ data }) => setOthers(data)).catch(() => {});
  }, [slug]);

  if (error) {
    return (
      <PremiumLayout>
        <div className="max-w-[1000px] mx-auto px-6 md:px-10 py-32 text-center">
          <div className="gold text-[11px] tracking-[.16em] uppercase">Not found</div>
          <div className="text-4xl mt-3">This post has moved on.</div>
          <Link to="/blog" className="mt-6 inline-block gold border-b border-[#C9A96E]/40 hover:border-[#C9A96E]">← Back to Studio Journal</Link>
        </div>
      </PremiumLayout>
    );
  }
  if (!post) {
    return <PremiumLayout><div className="max-w-[1000px] mx-auto px-6 md:px-10 py-32 text-center gold">Loading…</div></PremiumLayout>;
  }

  const related = others.filter((p) => p.slug !== post.slug).slice(0, 3);

  return (
    <PremiumLayout>
      {/* Hero */}
      {post.cover_image_url && (
        <section className="w-full">
          <div className="w-full aspect-[16/9] md:aspect-[21/9] overflow-hidden">
            <img src={post.cover_image_url} alt={post.title}
                 className="w-full h-full object-cover ken-burns" />
          </div>
        </section>
      )}

      {/* Meta + title */}
      <section className="max-w-[900px] mx-auto px-6 md:px-10 pt-12 pb-6" data-testid="blog-detail-header">
        <div className="flex items-center justify-between text-[11px] tracking-[.16em] uppercase text-[#9A9284]">
          <span className="gold">{post.tag || "Journal"}</span>
          <span>{formatDate(post.published_at || post.created_at)}</span>
        </div>
        <h1 className="text-4xl md:text-6xl leading-[1.05] mt-4">{post.title}</h1>
        <div className="mt-4 text-[#DDD5C4]">By <span className="cream">{post.author || "Studio DHP"}</span></div>
      </section>

      {/* Excerpt */}
      {post.excerpt && (
        <section className="max-w-[900px] mx-auto px-6 md:px-10 pb-4">
          <p className="text-2xl md:text-3xl italic leading-[1.35] gold" style={{ fontStyle: "italic" }}>
            {post.excerpt}
          </p>
        </section>
      )}

      {/* Body */}
      <article className="max-w-[820px] mx-auto px-6 md:px-10 py-12 prose-blog"
               data-testid="blog-detail-body"
               dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(post.body || "") }} />

      <style>{`
        .prose-blog { color: #DDD5C4; font-size: 18px; line-height: 1.75; }
        .prose-blog p { margin: 1em 0; }
        .prose-blog h2 { font-size: 30px; margin: 1.6em 0 0.4em; color: #F5F1EA; letter-spacing: -0.005em; }
        .prose-blog h3 { font-size: 24px; margin: 1.4em 0 0.3em; color: #F5F1EA; }
        .prose-blog blockquote { border-left: 3px solid #C9A96E; padding-left: 22px; color: #F5F1EA; font-size: 22px; font-style: italic; margin: 1.6em 0; }
        .prose-blog a { color: #C9A96E; border-bottom: 1px solid rgba(201,169,110,.4); }
        .prose-blog ul { list-style: disc; padding-left: 22px; margin: 1em 0; }
        .prose-blog img { max-width: 100%; margin: 1.5em 0; }
      `}</style>

      {/* Back / related */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-10 py-16 border-t border-[#1F1F1F]">
        <div className="flex items-center justify-between">
          <div className="gold text-[11px] tracking-[.16em] uppercase">More journal notes</div>
          <Link to="/blog" className="gold border-b border-[#C9A96E]/40 hover:border-[#C9A96E]">All posts →</Link>
        </div>
        {related.length > 0 && (
          <div className="grid md:grid-cols-3 gap-6 mt-10">
            {related.map((r) => (
              <Link key={r.id} to={`/blog/${r.slug}`} className="group border border-[#1F1F1F] overflow-hidden">
                {r.cover_image_url && (
                  <div className="relative aspect-[16/10] overflow-hidden">
                    <img src={r.cover_image_url} alt={r.title}
                         className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-[1.2s]" />
                  </div>
                )}
                <div className="p-5">
                  <div className="text-[11px] tracking-[.16em] uppercase gold">{r.tag || "Journal"}</div>
                  <div className="text-xl mt-2">{r.title}</div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>
    </PremiumLayout>
  );
}
